import { useCallback, useEffect, useRef } from "react";
import type { TextInput } from "react-native";

/**
 * Board-Aktion „Protokoll“: die Eingabezeile fokussieren, sobald sie im Baum steht.
 * Einmal pro Mount. Nach dem Fokus nimmt der Aufrufer focus=entry aus der URL,
 * damit ein späteres Remount nicht wieder auf das Gewichtsfeld springt.
 */
export function clearFocusEntryParam(router: {
  setParams: (params: { focus?: string }) => void;
}): void {
  router.setParams({ focus: undefined });
}

export function useFocusEntry(focusEntry: boolean, onFocused?: () => void) {
  const done = useRef(false);
  const frame = useRef<number | null>(null);
  const onFocusedRef = useRef(onFocused);
  onFocusedRef.current = onFocused;

  useEffect(() => {
    return () => {
      if (frame.current != null) cancelAnimationFrame(frame.current);
    };
  }, []);

  return useCallback((node: TextInput | null) => {
    if (node == null) {
      if (frame.current != null) cancelAnimationFrame(frame.current);
      frame.current = null;
      return;
    }
    if (!focusEntry || done.current) return;
    if (frame.current != null) cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => {
      frame.current = null;
      if (done.current) return;
      done.current = true;
      node.focus();
      const host = node as unknown as {
        scrollIntoView?: (options?: ScrollIntoViewOptions) => void;
      };
      host.scrollIntoView?.({ block: "nearest", inline: "nearest" });
      onFocusedRef.current?.();
    });
  }, [focusEntry]);
}
